import { useState, useEffect } from "react";
import type { ApiProviderProfile } from '../types/matchmaking.types';
import { useProviderImages } from '../hooks/useProviderImages';
import { useProviderProfile } from '../hooks/useProviderProfile';
import { useProviderFeedback } from '../hooks/useProviderFeedback';
import { formatRating, formatDistance, getServiceRanges, getReasoningText, parseServiceArea } from '../utils/formatting.utils';
import { parseSpecialties } from '../utils/profile-data.utils';
import { ProviderImage } from './MatchmakingResultCard/ProviderImage';
import { ProviderHeader } from './MatchmakingResultCard/ProviderHeader';
import { ServiceAreaDisplay } from './MatchmakingResultCard/ServiceAreaDisplay';
import { AIReasoningSection } from './MatchmakingResultCard/AIReasoningSection';
import { ExpandedProfileContent } from './MatchmakingResultCard/ExpandedProfileContent';
import { ActionButtons } from './MatchmakingResultCard/ActionButtons';
import { ShareToClientModal } from './ShareToClientModal';
import { ShareContactCardModal } from '@/features/contact-card-share/components/ShareContactCardModal';

type MatchmakingResultCardProps = {
  name: string;
  tags: string[];
  serviceArea: string;
  serviceType: string;
  aiRationale: string;
  matchLevel: "high" | "medium" | "low";
  matchLabel: string;
  imageSrc?: string;
  providerProfile?: ApiProviderProfile;
  searchId?: number | null;
  advisorId?: string;
  clientId?: string | null;
  preloadedImageUrl?: string | null;
};

const MatchmakingResultCard = ({
  name,
  serviceArea,
  serviceType,
  aiRationale,
  providerProfile,
  searchId,
  advisorId,
  clientId,
  preloadedImageUrl,
}: MatchmakingResultCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCountyTooltip, setShowCountyTooltip] = useState(false);
  const [showViewProfileTooltip, setShowViewProfileTooltip] = useState(false);
  const [showMessageTooltip, setShowMessageTooltip] = useState(false);
  const [showHeartTooltip, setShowHeartTooltip] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [showCrossTooltip, setShowCrossTooltip] = useState(false);
  const [showDistanceTooltip, setShowDistanceTooltip] = useState(false);
  const [showRatingTooltip, setShowRatingTooltip] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  // Feature 071: off-platform sibling of the share-to-client action.
  const [isContactCardModalOpen, setIsContactCardModalOpen] = useState(false);

  const {
    imageUrl,
    isImageLoading,
    shouldLoadImage,
    setShouldLoadImage,
    profileImages,
    setProfileImages,
    activeImageIndex,
    setActiveImageIndex,
    setIsImageLoading,
    handleImageLoad,
    handleImageError,
  } = useProviderImages({
    preloadedImageUrl,
    providerProfile,
    isExpanded,
  });

  const {
    profileLoading,
    fetchedProfile,
    isProfileImageLoading: profileImageLoading,
    handleViewProfileClick: handleViewProfileClickBase,
  } = useProviderProfile({
    providerProfile,
    isExpanded,
    setProfileImages,
  });

  // Reset expanded state when provider changes
  useEffect(() => {
    setIsExpanded(false);
  }, [providerProfile?.provider_id]);

  const formattedRating = formatRating(providerProfile);
  const formattedDistance = formatDistance(providerProfile);
  const serviceRanges = getServiceRanges(providerProfile, serviceType);
  const reasoningText = getReasoningText(aiRationale, providerProfile);
  const parsedServiceArea = parseServiceArea(serviceArea);

  const {
    isHearted,
    isCrossed,
    handleHeartClick,
    handleCrossClick,
  } = useProviderFeedback({
    searchId,
    advisorId,
    clientId,
    providerId: providerProfile?.provider_id,
    providerUserId: fetchedProfile?.userId,
  });

  const handleViewProfileClick = async () => {
    if (isExpanded) {
      setIsExpanded(false);
      return;
    }

    await handleViewProfileClickBase();
    setIsExpanded(true);
  };

  // Get data from fetched profile or providerProfile
  const displayOverview = fetchedProfile?.companyOverview ?? fetchedProfile?.providerBio ?? null;
  const displaySpecialties = parseSpecialties(fetchedProfile, providerProfile);

  return (
    <article className="rounded-3xl border border-[#E5E7EB] bg-white shadow-[0_24px_80px_rgba(15,23,42,0.06)]">
      <div className="px-8 pt-8 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-[150px_minmax(0,1fr)] gap-6 items-start">
          {/* Primary image column */}
          <div className="flex flex-col gap-3 items-center lg:items-start">
            <ProviderImage
              imageUrl={imageUrl}
              name={name}
              isImageLoading={isImageLoading}
              shouldLoadImage={shouldLoadImage}
              providerId={providerProfile?.provider_id}
              onImageLoad={() => setIsImageLoading(false)}
              onImageError={() => {
                setIsImageLoading(false);
                if (imageUrl) {
                  console.warn('Failed to load image:', imageUrl);
                }
              }}
              onShouldLoadChange={setShouldLoadImage}
            />
          </div>

          {/* Heading + Badges + Service Area + AI reasoning */}
          <div className="flex flex-col gap-4 min-w-0">
            <div className="space-y-3">
              {/* Two-column layout: Left (Header + Highlights) | Right (Service Area) */}
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-4 items-start">
                {/* Left Column: Header and Highlights */}
                <ProviderHeader
                  name={name}
                  serviceRanges={serviceRanges}
                  badges={providerProfile?.badges}
                  providerId={providerProfile?.provider_id}
                />
                
                {/* Right Column: Service Area */}
                <ServiceAreaDisplay
                  displayText={parsedServiceArea.displayText}
                  hasMore={parsedServiceArea.hasMore}
                  remainingCounties={parsedServiceArea.remainingCounties}
                  showTooltip={showCountyTooltip}
                  onTooltipEnter={() => setShowCountyTooltip(true)}
                  onTooltipLeave={() => setShowCountyTooltip(false)}
                />
              </div>
              
              {/* AI Reasoning - Full Width */}
              <AIReasoningSection
                reasoningText={reasoningText}
                isExpanded={isExpanded}
              />
            </div>
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="px-8 pl-16 pb-6 space-y-6">
          <ExpandedProfileContent
            profileLoading={profileLoading}
            displayOverview={displayOverview}
            displaySpecialties={displaySpecialties}
            profileImages={profileImages}
            activeImageIndex={activeImageIndex}
            isProfileImageLoading={profileImageLoading}
            name={name}
            providerId={providerProfile?.provider_id}
            onImageIndexChange={setActiveImageIndex}
            onImageLoad={handleImageLoad}
            onImageError={handleImageError}
          />
        </div>
      )}
      <div className="h-px w-full bg-[#f9fafc] border-t border-[#dbdbdb]"/>
      <div className="px-8 py-3 bg-[#f9fafc] space-y-6 rounded-b-3xl">
        <ActionButtons
          isExpanded={isExpanded}
          profileLoading={profileLoading}
          viewMoreLabel={isExpanded ? "Hide Profile" : "View Profile"}
          onViewProfileClick={handleViewProfileClick}
          showViewProfileTooltip={showViewProfileTooltip}
          onViewProfileTooltipEnter={() => setShowViewProfileTooltip(true)}
          onViewProfileTooltipLeave={() => setShowViewProfileTooltip(false)}
          formattedDistance={formattedDistance}
          showDistanceTooltip={showDistanceTooltip}
          onDistanceTooltipEnter={() => setShowDistanceTooltip(true)}
          onDistanceTooltipLeave={() => setShowDistanceTooltip(false)}
          formattedRating={formattedRating}
          showRatingTooltip={showRatingTooltip}
          onRatingTooltipEnter={() => setShowRatingTooltip(true)}
          onRatingTooltipLeave={() => setShowRatingTooltip(false)}
          providerId={providerProfile?.provider_id}
          providerName={name}
          clientId={clientId}
          isHearted={isHearted}
          isCrossed={isCrossed}
          onHeartClick={handleHeartClick}
          onCrossClick={handleCrossClick}
          showMessageTooltip={showMessageTooltip}
          onMessageTooltipEnter={() => setShowMessageTooltip(true)}
          onMessageTooltipLeave={() => setShowMessageTooltip(false)}
          showHeartTooltip={showHeartTooltip}
          onHeartTooltipEnter={() => setShowHeartTooltip(true)}
          onHeartTooltipLeave={() => setShowHeartTooltip(false)}
          showShareTooltip={showShareTooltip}
          onShareTooltipEnter={() => setShowShareTooltip(true)}
          onShareTooltipLeave={() => setShowShareTooltip(false)}
          onShareClick={() => setIsShareModalOpen(true)}
          onSendContactCardClick={
            providerProfile?.provider_id ? () => setIsContactCardModalOpen(true) : undefined
          }
          showCrossTooltip={showCrossTooltip}
          onCrossTooltipEnter={() => setShowCrossTooltip(true)}
          onCrossTooltipLeave={() => setShowCrossTooltip(false)}
        />
      </div>
      <ShareToClientModal
        open={isShareModalOpen}
        onOpenChange={setIsShareModalOpen}
        providerId={providerProfile?.provider_id ?? null}
        providerName={name}
        clientId={clientId || null}
      />
      {providerProfile?.provider_id && (
        // The card carries an id and a name but no phone, email or title, so the
        // modal fetches the rest itself rather than being handed a half-card.
        <ShareContactCardModal
          open={isContactCardModalOpen}
          onOpenChange={setIsContactCardModalOpen}
          serviceProviderId={providerProfile.provider_id}
        />
      )}
    </article>
  );
};

export default MatchmakingResultCard;

